import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface DeploymentRow {
  readonly deploymentId: string;
  readonly deploymentCode: string;
  readonly releaseId: string | null;
  readonly environmentId: string | null;
  readonly environmentType: string;
  readonly status: string;
  readonly triggeredBy: string;
  readonly completedAt: string | null;
}

const STATUSES = ['', 'PENDING', 'IN_PROGRESS', 'DEPLOYED', 'VALIDATED', 'FAILED', 'ROLLED_BACK'];
const ENV_TYPES = ['', 'DEVELOPMENT', 'QA', 'UAT', 'STAGING', 'PRE_PRODUCTION', 'PRODUCTION', 'DISASTER_RECOVERY', 'SANDBOX'];
const CREATE_ENV_TYPES = ENV_TYPES.filter(Boolean);
const RESULT_STATUSES = ['DEPLOYED', 'FAILED'];

/**
 * M32 — Deployment Dashboard (§31 / §19-20). Register of deployment executions
 * (T425) across environments with trigger source and lifecycle status. Supports
 * creating a deployment and driving it through authorise → start → record-result
 * → validate, each wired to the guarded /v1/devops/deployments write API.
 * Filterable by status and target environment type; production rows without a
 * linked release surface BR-03 / BR-07 governance violations.
 */
export function DeploymentDashboardPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<DeploymentRow[]>([]);
  const [status, setStatus] = useState('');
  const [environmentType, setEnvironmentType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newEnvType, setNewEnvType] = useState('DEVELOPMENT');
  const [newReleaseId, setNewReleaseId] = useState('');
  const [result, setResult] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (environmentType) params.set('environmentType', environmentType);
    const qs = params.toString();
    apiRequest<{ data: DeploymentRow[] }>(`/v1/devops/deployments${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, environmentType, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m32.deployment.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/deployments`, {
        method: 'POST',
        body: { environmentType: newEnvType, releaseId: newReleaseId.trim() || undefined },
        token,
      });
      setNotice(tr('m32.deployment.created'));
      setNewReleaseId('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: DeploymentRow, path: string, label: string, body: Record<string, unknown> = {}): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/deployments/${r.deploymentId}/${path}`, { method: 'POST', body, token });
      setNotice(tr('m32.deployment.actNotice', { code: r.deploymentCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="deployment-dashboard">
      <h1>{tr('m32.deployment.title')}</h1>

      <section aria-label="create-deployment">
        <h2>{tr('m32.deployment.createTitle')}</h2>
        <form aria-label="deployment-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m32.deployment.environment')}
            <select aria-label="field-environment-type" value={newEnvType} onChange={(e) => setNewEnvType(e.target.value)}>
              {CREATE_ENV_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m32.deployment.fieldReleaseId')}<input aria-label="field-release-id" value={newReleaseId} onChange={(e) => setNewReleaseId(e.target.value)} /></label>
          <button type="submit" aria-label="submit-deployment" disabled={busy}>{busy ? tr('m32.deployment.creating') : tr('m32.deployment.createBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="deployment-error">{actionError}</p>}
        {notice && <p aria-label="deployment-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m32.deployment.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m32.deployment.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.deployment.environment')}
          <select aria-label="filter-environment-type" value={environmentType} onChange={(e) => setEnvironmentType(e.target.value)}>
            {ENV_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.deployment.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.deployment.loading')}</p> : (
        <>
          <p aria-label="deployment-count">{tr('m32.deployment.count', { count: rows.length })}</p>
          <table aria-label="deployment-table">
            <thead><tr><th>{tr('m32.deployment.thCode')}</th><th>{tr('m32.deployment.environment')}</th><th>{tr('m32.deployment.thType')}</th><th>{tr('m32.deployment.thTrigger')}</th><th>{tr('m32.deployment.thDeployedAt')}</th><th>{tr('m32.deployment.status')}</th><th>{tr('m32.deployment.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.deploymentId} data-status={r.status}>
                  <td>{r.deploymentCode}</td><td>{r.environmentId ?? '—'}</td><td>{r.environmentType}</td>
                  <td>{r.triggeredBy}</td><td>{at(r.completedAt)}</td><td>{r.status}</td>
                  <td>
                    {r.status === 'PENDING' && (
                      <>
                        <button type="button" aria-label={`authorize-${r.deploymentCode}`} onClick={() => void act(r, 'authorize', tr('m32.deployment.act.authorised'))}>{tr('m32.deployment.authorise')}</button>
                        <button type="button" aria-label={`start-${r.deploymentCode}`} onClick={() => void act(r, 'start', tr('m32.deployment.act.started'))}>{tr('m32.deployment.start')}</button>
                      </>
                    )}
                    {r.status === 'IN_PROGRESS' && (
                      <>
                        <select aria-label={`result-status-${r.deploymentCode}`} value={result[r.deploymentId] ?? 'DEPLOYED'} onChange={(e) => setResult((m) => ({ ...m, [r.deploymentId]: e.target.value }))}>
                          {RESULT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button type="button" aria-label={`result-${r.deploymentCode}`} onClick={() => void act(r, 'result', tr('m32.deployment.act.resultRecorded'), { status: result[r.deploymentId] ?? 'DEPLOYED' })}>{tr('m32.deployment.recordResult')}</button>
                      </>
                    )}
                    {r.status === 'DEPLOYED' && (
                      <button type="button" aria-label={`validate-${r.deploymentCode}`} onClick={() => void act(r, 'validate', tr('m32.deployment.act.validated'), { passed: true })}>{tr('m32.deployment.validate')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
